import React, { useState, useEffect } from 'react';
import { Newspaper, Plus, Search, Loader, RefreshCw, X } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    avatar_url: ''
  });

  const token = sessionStorage.getItem('adminToken');

  const fetchNews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setNews(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching news:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/news`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setFormData({ title: '', subtitle: '', image_url: '', avatar_url: '' });
        setIsModalOpen(false);
        fetchNews();
      }
    } catch (err) {
      console.error("Error creating news:", err);
      alert(err.response?.data?.message || "Failed to create news");
    }
    setIsSubmitting(false);
  };

  const filteredNews = news.filter(n => n.title.toLowerCase().includes(search.toLowerCase()) || (n.subtitle && n.subtitle.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-pink-100 rounded-xl">
            <Newspaper className="text-pink-600" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">News Management</h1>
            <p className="text-slate-500 text-sm">Create and manage news for the mobile app</p>
          </div>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg shadow-pink-500/30 hover:scale-105 transition flex items-center gap-2 font-semibold"
        >
          <Plus size={20} /> Create News
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="relative w-72">
            <Search className="absolute left-3 top-3 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search news..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition"
            />
          </div>
          <button onClick={fetchNews} className="text-slate-500 hover:text-pink-600 p-2 bg-slate-50 rounded-lg hover:bg-pink-50 transition">
            <RefreshCw size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-pink-500" size={40} />
          </div>
        ) : filteredNews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNews.map(item => (
              <div key={item.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition group relative">
                {item.image_url ? (
                  <img src={item.image_url} alt="news" className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-tr from-pink-100 to-purple-100 flex items-center justify-center">
                    <Newspaper size={40} className="text-pink-300" />
                  </div>
                )}
                <div className="p-5 relative">
                  <div className="absolute -top-10 right-4 p-1 bg-white rounded-full shadow-md">
                    <img src={item.avatar_url || "https://randomuser.me/api/portraits/lego/1.jpg"} alt="avatar" className="w-16 h-16 rounded-full object-cover border-2 border-pink-100" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2 pr-20">{item.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2">{item.subtitle}</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center text-xs text-slate-400">
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-600 rounded-lg font-medium">Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-500">
            <Newspaper size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">No news found.</p>
            <p className="text-sm">Click "Create News" to add a new entry.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-fade-in">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Newspaper size={24} className="text-pink-500" />
                Create News
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition">
                <X size={20} className="text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleInputChange} 
                  required
                  placeholder="Enter news title"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Subtitle</label>
                <textarea 
                  name="subtitle" 
                  value={formData.subtitle} 
                  onChange={handleInputChange} 
                  rows={3}
                  placeholder="Enter news subtitle or brief description"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Main Image URL</label>
                <input 
                  type="url" 
                  name="image_url" 
                  value={formData.image_url} 
                  onChange={handleInputChange} 
                  placeholder="https://example.com/image.jpg"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Author Avatar URL</label>
                <input 
                  type="url" 
                  name="avatar_url" 
                  value={formData.avatar_url} 
                  onChange={handleInputChange} 
                  placeholder="https://example.com/avatar.jpg"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition"
                />
              </div>

              <div className="pt-4 flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition shadow-lg shadow-pink-500/20 disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isSubmitting ? <Loader className="animate-spin" size={20} /> : "Publish News"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
